/**
 * The DOMHelper class abstracts some DOM references/modifications
 * @class
 */
class DOMHelper {

    /**
     * Insert newNode directly after referenceNode
     * @param {Node} newNode - The node to be inserted
     * @param {Node} referenceNode - The node to apend newNode after
     */
    static insertAfter(newNode, referenceNode) {
        referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling);
    }

    /**
     * Returns the offset top of a given element
     * @param {Element} element - The element
     * @returns {Number} - Numeric value, representing the offset top
     */
    static getOffsetTop(element) {

        let offsetTop = element.offsetTop;

        while (element === element.offsetParent) {
            if (!isNaN(element.offsetTop)) {
                offsetTop += element.offsetTop;
            }
        }

        return offsetTop;

    }

    /**
     * @param {HTMLElement} elem - element
     * @param {string} selector - selector
     * @returns {boolean|HTMLElement} - boolean or an HTMLElement
     */
    static closest(elem, selector) {

        let matchesSelector = elem.matches || elem.webkitMatchesSelector || elem.mozMatchesSelector || elem.msMatchesSelector;

        while (elem) {
            if (!matchesSelector.bind(elem)(selector)) {
                elem = elem.parentElement;
            } else {
                return elem;
            }
        }

        return false;
    }

    /**
     * @param {string} element - element
     * @param {string} property - property
     * @param {string} value - value
     */
    static setVendor(element, property, value) {

        let prefixes = ['-webkit-', '-moz-', '-ms-'];
        element.style[property] = value;

        prefixes.forEach(prefix => {
            element.style[prefix + property] = value;
        });

    }

    /**
     * @param {string} property - property
     * @param {string} value - value
     * @returns {{string}} styles - styles
     */
    static getPrefixedStyle(property, value) {

        let prefixes = ['-webkit-', '-moz-', '-ms-'];
        let styles = {};

        styles[property] = value;
        prefixes.forEach(prefix => {
            styles[prefix + property] = value;
        });

        return styles;

    }

    /**
     * @param {string} styles - style
     * @returns {object} object - object
     */
    static getPrefixedStyles(styles) {

        return Object.keys(styles).reduce((res, prop) => {
            return Object.assign(res, DOMHelper.getPrefixedStyle(prop, styles[prop]));
        }, {});

    }

    /**
     * @param {HTMLElement} element - element offsetHeight
     * @returns {*} height - height
     */
    static usedHeight(element) {

        let elems = [].slice.call(element.children);

        return elems.reduce((height, elem) => height + elem.offsetHeight, 0);

    }

    /**
     * @param {HTMLElement} elems - elems
     * @param {string} styles - styles
     * @returns {Function} function - for each
     */
    static temporaryStyles(elems, styles) {

        let oldStyles = {};

        elems = [].concat(elems);
        elems.forEach(el => {
            Object.keys(styles).forEach(prop => {
                oldStyles[prop] = el.style[prop] || '';
            });
            Object.keys(styles).forEach(prop => {
                el.style[prop] = styles[prop];
            });
        });

        return function() {
            elems.forEach(el => {
                Object.keys(oldStyles).forEach(prop => {
                    el.style[prop] = oldStyles[prop];
                });
            });
        };

    }

    /**
     * @param {HTMLElement} element - element
     * @returns {Number}  pos - position
     */
    static getScrollPosition(element) {
        let pos = element.scrollY || element.scrollTop;

        if (pos === undefined && element === window) {
            pos = document.documentElement.scrollTop || document.body.parentNode.scrollTop || document.body.scrollTop;
        }

        return pos;
    }

    /**
     * @param {HTMLElement} element - element
     * @returns {number} scrollTargetY - scrollTargetY
     */
    static getPosition(element) {
        let scrollY = window.scrollY || window.pageYOffset;
        let scrollTargetY = element.getBoundingClientRect().top + scrollY;

        return scrollTargetY;
    }

    /**
     * @param {HTMLElement} element - html element to wrap
     * @param {string} node - name of html node to wrap with
     * @returns {Element} - wrapped element
     */
    static wrapChildNodes(element, node) {
        let wrapper = document.createElement(node);
        let elementTowrap = element.innerHTML;
        wrapper.innerHTML = elementTowrap;
        element.innerHTML = '';
        element.appendChild(wrapper);

        return wrapper;
    }

    /**
     * @param {HTMLElement} element - html element to setPrefixedStyle
     * @param {string} property - property
     * @param {string} value - value
     * @returns {Function} Function - Function
     */
    static setPrefixedStyle(element, property, value) {
        const prefixes = ['', '-webkit-', '-moz-', '-ms-'];

        return function() {
            prefixes.forEach(prefix => element.style[prefix + property] === value);
        };
    }

    /**
     * @param {HTMLElement} element - html element to reflow
     * @returns {number} number - height
     */
    static reflow(element) {
        return element.offsetHeight;
    }

}

export default DOMHelper;
